import { Button, Col, Form, Input, Row } from 'antd';
import { useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import { NotificationContext } from '../../providers/NotificationProvider';
import { requestTrial } from '../../features/user/userSlice';
import logo from '../../assets/images/logo.png';
import { useTranslation } from 'react-i18next';
import { setLanguage } from '../../i18n';
import LanguageTabs from '../shared/LanguageTabs';
import { useTurnstileCaptcha } from './useTurnstileCaptcha';

type TrialFormFields = {
  first_name?: string;
  last_name?: string;
  email?: string;
  country_code?: string;
  phone?: string;
  company?: string;
};

const RequestTrialForm: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { triggerNotification } = useContext(NotificationContext);
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const lang = (searchParams.get('lang') || '').trim();
  const { captchaRef, captchaToken, captchaReady, resetCaptcha, turnstileSiteKey } = useTurnstileCaptcha();

  useEffect(() => {
    const currentCode = form.getFieldValue('country_code');
    if (!currentCode) {
      form.setFieldsValue({
        country_code: t('Código de país por defecto'),
      });
    }
  }, [form, t]);

  useEffect(() => {
    if (!lang) {
      return;
    }

    setLanguage(lang);
  }, [lang]);

  const handleSubmit = (values: TrialFormFields) => {
    if (!turnstileSiteKey) {
      triggerNotification('error', t('Error'), t('El captcha no está configurado.'), 'bottomRight');
      return;
    }

    if (!captchaToken) {
      triggerNotification('error', t('Error'), t('Por favor valida el captcha.'), 'bottomRight');
      return;
    }

    dispatch(
      requestTrial({
        first_name: values.first_name || '',
        last_name: values.last_name || '',
        email: values.email || '',
        country_code: values.country_code || '',
        phone: values.phone || '',
        company: values.company || '',
        captcha_token: captchaToken,
      }) as any
    )
      .then((response: any) => {
        if (requestTrial.fulfilled.match(response)) {
          form.resetFields();
          resetCaptcha();
          triggerNotification('success', t('Éxito'), response.payload.message, 'bottomRight');
          return;
        }

        resetCaptcha();
        triggerNotification('error', t('Error'), response.payload?.message || t('No se pudo enviar la solicitud.'), 'bottomRight');
      })
      .catch((err: any) => {
        resetCaptcha();
        triggerNotification('error', t('Error'), err.message, 'bottomRight');
      });
  };

  return (
    <div className='trial-request-page'>
      <div style={{ position: 'absolute', top: 16, right: 16 }}>
        <LanguageTabs compact />
      </div>
      <Row className='trial-request-layout'>
        <Col className='trial-request-panel'>
          <img src={logo} alt='logo' className='logo' />
          <div className='trial-request-copy'>
            <h1>{t('Solicitar Prueba')}</h1>
            <p>{t('Déjanos tus datos y te contactaremos para habilitar tu prueba del software.')}</p>
          </div>

          <Form form={form} name='request-trial' layout='vertical' onFinish={handleSubmit} autoComplete='off'>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item<TrialFormFields>
                  label={t('Nombre')}
                  name='first_name'
                  rules={[{ required: true, message: t('Por favor ingresa tu nombre.') }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item<TrialFormFields>
                  label={t('Apellido')}
                  name='last_name'
                  rules={[{ required: true, message: t('Por favor ingresa tu apellido.') }]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item<TrialFormFields>
              label={t('Correo electrónico')}
              name='email'
              rules={[
                { required: true, message: t('Por favor ingresa tu correo electrónico.') },
                { type: 'email', message: t('Por favor ingresa un correo electrónico válido.') },
                {
                  validator: async (_, value) => {
                    if (!value) {
                      return Promise.resolve();
                    }
                    const normalized = String(value).trim();
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
                    if (!emailRegex.test(normalized)) {
                      return Promise.reject(new Error(t('Por favor ingresa un correo electrónico válido.')));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input />
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item<TrialFormFields>
                  label={t('Indicativo')}
                  name='country_code'
                  rules={[
                    { required: true, message: t('Por favor ingresa el indicativo.') },
                    {
                      pattern: /^\+?\d{1,4}$/,
                      message: t('Ingresa un indicativo válido.'),
                    },
                  ]}
                  normalize={(value) => {
                    if (!value) {
                      return value;
                    }
                    const digits = String(value).replace(/\D/g, '').slice(0, 4);
                    return digits ? `+${digits}` : '';
                  }}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={16}>
                <Form.Item<TrialFormFields>
                  label={t('Teléfono')}
                  name='phone'
                  rules={[
                    { required: true, message: t('Por favor ingresa tu teléfono.') },
                    {
                      validator: async (_, value) => {
                        if (!value) {
                          return Promise.resolve();
                        }
                        const digits = String(value).replace(/\D/g, '');
                        if (digits.length < 10) {
                          return Promise.reject(new Error(t('El teléfono debe tener al menos 10 dígitos.')));
                        }
                        if (digits.length > 15) {
                          return Promise.reject(new Error(t('El teléfono no puede tener más de 15 dígitos.')));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  normalize={(value) => (value ? String(value).replace(/\D/g, '') : value)}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item<TrialFormFields>
              label={t('Empresa')}
              name='company'
              rules={[{ required: true, message: t('Por favor ingresa tu empresa.') }]}
            >
              <Input />
            </Form.Item>

            <div className='trial-captcha-wrapper'>
              {turnstileSiteKey ? (
                <div ref={captchaRef} />
              ) : (
                <small>{t('Configura REACT_APP_TURNSTILE_SITE_KEY para habilitar el captcha.')}</small>
              )}
            </div>

            <div className='trial-request-actions'>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button type='primary' htmlType='submit' disabled={!captchaReady && !!turnstileSiteKey}>
                  {t('Enviar solicitud')}
                </Button>
              </Form.Item>
              <Link to='/'>{t('Volver al inicio de sesión')}</Link>
            </div>
          </Form>
        </Col>
      </Row>
    </div>
  );
};

export default RequestTrialForm;
